import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Card,
  CardActionArea,
  CardContent,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Chip,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Warning as WarningIcon,
  ListAlt as TemplateIcon,
  FolderOpen as ImportIcon,
} from '@mui/icons-material';
import { axiosService } from 'shared/helpers/axios';
import ImportProposalDialog from './ImportProposalDialog';

const GENERIC_ID = 'generic';
const GENERIC_COLOR = '#2e7d32';
const PROGRAM_CARD_COLOR = '#5c35b0';

const PROGRAM_COLORS = {
  horizon_europe: '#003399',
  erc: '#b5860c',
  tubitak_1001: '#b7131e',
};

const USER_TEMPLATE_COLOR = '#6d4c41';

const PUBLICATION_TEMPLATE_COLOR = '#2e7d32';

const ProposalTemplateDialog = ({
  open, onClose, proposalId, existingSectionCount = 0, onSuccess, documentType = 'proposal', recommendedTemplate,
}) => {
  const isPublication = documentType === 'publication';
  const [templates, setTemplates] = useState([]);
  const [selected, setSelected] = useState(null);
  const [programSelected, setProgramSelected] = useState('');
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [importOpen, setImportOpen] = useState(false);

  useEffect(() => {
    if (open) {
      // Publications start with the target journal's recommended outline selected
      setSelected(isPublication && recommendedTemplate ? recommendedTemplate : null);
      setProgramSelected('');
      setError(null);
      setConfirming(false);
      loadTemplates();
    }
  }, [open]);

  const loadTemplates = async () => {
    setLoading(true);
    try {
      const res = await axiosService.get('/proposal/proposals/templates/', { params: { document_type: documentType } });
      setTemplates(res.data);
    } catch {
      setError('Failed to load templates.');
    }
    setLoading(false);
  };

  // Sections with the same title keep their content; the server only asks for
  // confirmation when written sections have no counterpart in the new template.
  const [conflicts, setConflicts] = useState([]);
  const handleUseTemplate = () => {
    if (!selected) return;
    applyTemplate(false);
  };

  const applyTemplate = async (force) => {
    setApplying(true);
    setError(null);
    try {
      const res = await axiosService.post(`/proposal/proposals/${proposalId}/apply_template/`, {
        template_id: selected,
        force,
      });
      onSuccess?.(res.data);
      onClose();
    } catch (err) {
      if (err.response?.status === 409 && err.response.data?.code === 'sections_would_be_removed') {
        setConflicts(err.response.data.sections || []);
        setConfirming(true);
      } else {
        setError(err.response?.data?.error || 'Failed to apply template.');
        setConfirming(false);
      }
    }
    setApplying(false);
  };

  const selectedTemplate = templates.find(t => t.id === selected);
  const genericTemplate = templates.find(t => t.id === GENERIC_ID);
  const programTemplates = templates.filter(t => !t.is_user_defined && t.id !== GENERIC_ID);
  const userTemplates = templates.filter(t => t.is_user_defined);

  if (confirming) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningIcon color="warning" />
          Remove sections with written content?
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 1.5 }}>
            The <strong>{selectedTemplate?.name}</strong> format has no section with these titles, so they and their
            text would be removed:
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2.5, mb: 1.5 }}>
            {conflicts.map(c => (
              <li key={c.id}><Typography variant="body2"><strong>{c.title}</strong> — {c.word_count} words</Typography></li>
            ))}
          </Box>
          <Typography variant="body2" color="text.secondary">
            Sections whose titles exist in both formats keep their content. To keep this text, copy it into another
            section first, or rename the section to match a title in the new format.
          </Typography>
          {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirming(false)}>Go Back</Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => applyTemplate(true)}
            disabled={applying}
            startIcon={applying ? <CircularProgress size={18} /> : undefined}
          >
            {applying ? 'Applying…' : 'Remove and apply'}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <TemplateIcon color="primary" />
        Start from a Template
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Choose a template to automatically generate a full section structure.
              You can add, edit, or remove sections afterwards.
            </Typography>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {/* Publications: one card per article type */}
            {isPublication && (
              <Grid container spacing={2} sx={{ mb: userTemplates.length ? 2 : 0 }}>
                {templates.filter(t => !t.is_user_defined).map(t => (
                  <Grid item xs={12} sm={6} key={t.id}>
                    <Card
                      variant={selected === t.id ? 'elevation' : 'outlined'}
                      elevation={selected === t.id ? 6 : 0}
                      sx={{
                        border: selected === t.id ? `2px solid ${PUBLICATION_TEMPLATE_COLOR}` : undefined,
                        transition: 'all 0.15s',
                        height: '100%',
                      }}
                    >
                      <CardActionArea onClick={() => setSelected(t.id)} sx={{ height: '100%' }}>
                        <CardContent>
                          <Box display="flex" alignItems="center" gap={1} mb={1} flexWrap="wrap">
                            <Chip
                              label={t.icon}
                              size="small"
                              sx={{ bgcolor: PUBLICATION_TEMPLATE_COLOR, color: '#fff', fontWeight: 700, fontSize: 11 }}
                            />
                            <Typography variant="subtitle1" fontWeight={600}>{t.name}</Typography>
                            {t.id === recommendedTemplate && (
                              <Chip label="Recommended for your journal" size="small" color="primary" variant="outlined" />
                            )}
                          </Box>
                          <Typography variant="body2" color="text.secondary">{t.description}</Typography>
                        </CardContent>
                      </CardActionArea>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}

            {!isPublication && (
            <Grid container spacing={2} sx={{ mb: userTemplates.length ? 2 : 0 }}>
              {/* Generic Research Proposal */}
              {genericTemplate && (
                <Grid item xs={12} sm={6}>
                  <Card
                    variant={selected === GENERIC_ID ? 'elevation' : 'outlined'}
                    elevation={selected === GENERIC_ID ? 6 : 0}
                    sx={{
                      border: selected === GENERIC_ID ? `2px solid ${GENERIC_COLOR}` : undefined,
                      transition: 'all 0.15s',
                      height: '100%',
                    }}
                  >
                    <CardActionArea
                      onClick={() => { setSelected(GENERIC_ID); setProgramSelected(''); }}
                      sx={{ height: '100%' }}
                    >
                      <CardContent>
                        <Box display="flex" alignItems="center" gap={1} mb={1}>
                          <Chip
                            label="GP"
                            size="small"
                            sx={{ bgcolor: GENERIC_COLOR, color: '#fff', fontWeight: 700, fontSize: 11 }}
                          />
                          <Typography variant="subtitle1" fontWeight={600}>
                            {genericTemplate.name}
                          </Typography>
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                          {genericTemplate.description}
                        </Typography>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Grid>
              )}

              {/* Program Template card with dropdown */}
              {programTemplates.length > 0 && (
                <Grid item xs={12} sm={6}>
                  <Card
                    variant={programSelected ? 'elevation' : 'outlined'}
                    elevation={programSelected ? 6 : 0}
                    sx={{
                      border: programSelected ? `2px solid ${PROGRAM_CARD_COLOR}` : undefined,
                      transition: 'all 0.15s',
                      height: '100%',
                    }}
                  >
                    <CardContent>
                      <Box display="flex" alignItems="center" gap={1} mb={1}>
                        <Chip
                          label="PRG"
                          size="small"
                          sx={{ bgcolor: PROGRAM_CARD_COLOR, color: '#fff', fontWeight: 700, fontSize: 11 }}
                        />
                        <Typography variant="subtitle1" fontWeight={600}>
                          Program Template
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                        Apply a structure tailored to a specific funding program.
                      </Typography>
                      <FormControl fullWidth size="small">
                        <InputLabel>Select program</InputLabel>
                        <Select
                          value={programSelected}
                          label="Select program"
                          onChange={(e) => {
                            const val = e.target.value;
                            setProgramSelected(val);
                            setSelected(val || null);
                          }}
                        >
                          {programTemplates.map(t => (
                            <MenuItem key={t.id} value={t.id}>
                              <Box display="flex" alignItems="center" gap={1}>
                                <Box
                                  sx={{
                                    width: 10,
                                    height: 10,
                                    borderRadius: '50%',
                                    bgcolor: PROGRAM_COLORS[t.id] || '#555',
                                    flexShrink: 0,
                                  }}
                                />
                                {t.name}
                              </Box>
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </CardContent>
                  </Card>
                </Grid>
              )}
            </Grid>
            )}

            {/* Saved (user-defined) templates */}
            {userTemplates.length > 0 && (
              <>
                <Divider sx={{ my: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ px: 1, textTransform: 'uppercase', letterSpacing: 1 }}>
                    Saved Templates
                  </Typography>
                </Divider>
                <Grid container spacing={2}>
                  {userTemplates.map(t => {
                    const isSelected = selected === t.id;
                    return (
                      <Grid item xs={12} sm={6} key={t.id}>
                        <Card
                          variant={isSelected ? 'elevation' : 'outlined'}
                          elevation={isSelected ? 6 : 0}
                          sx={{ border: isSelected ? `2px solid ${USER_TEMPLATE_COLOR}` : undefined, transition: 'all 0.15s', height: '100%' }}
                        >
                          <CardActionArea
                            onClick={() => { setSelected(t.id); setProgramSelected(''); }}
                            sx={{ height: '100%' }}
                          >
                            <CardContent>
                              <Box display="flex" alignItems="center" gap={1} mb={1}>
                                <Chip
                                  label="SAVED"
                                  size="small"
                                  sx={{ bgcolor: USER_TEMPLATE_COLOR, color: '#fff', fontWeight: 700, fontSize: 11 }}
                                />
                                <Typography variant="subtitle1" fontWeight={600}>{t.name}</Typography>
                              </Box>
                              <Typography variant="body2" color="text.secondary">{t.description}</Typography>
                            </CardContent>
                          </CardActionArea>
                        </Card>
                      </Grid>
                    );
                  })}
                </Grid>
              </>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ display: 'flex', justifyContent: 'space-between', px: 3 }}>
        <Button
          startIcon={<ImportIcon />}
          onClick={() => setImportOpen(true)}
          color="secondary"
        >
          Import Proposal Document
        </Button>
        <Box display="flex" gap={1}>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleUseTemplate}
            disabled={!selected || applying}
            startIcon={applying ? <CircularProgress size={18} /> : <TemplateIcon />}
          >
            {applying ? 'Generating…' : 'Use This Template'}
          </Button>
        </Box>
      </DialogActions>

      <ImportProposalDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        proposalId={proposalId}
        existingSectionCount={existingSectionCount}
        onSuccess={(data) => {
          setImportOpen(false);
          onSuccess?.(data);
          onClose();
        }}
      />
    </Dialog>
  );
};

export default ProposalTemplateDialog;
